import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TurndownService from 'turndown';
import { marked } from 'marked';
import ReactMarkdown from 'react-markdown';
import { useIntl } from 'react-intl';

function RichMarkdownInput({
    initialMarkdown,
    onChangeMarkdown,
    canEdit,
    undoLabel,
    redoLabel,
}) {
    const intl = useIntl();
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkUrlInput, setLinkUrlInput] = useState('');
    const linkUrlInputRef = useRef(null);
    const boldLabel = intl.formatMessage({ id: 'newsQueue.richBody.bold' });
    const italicLabel = intl.formatMessage({ id: 'newsQueue.richBody.italic' });
    const linkButtonLabel = intl.formatMessage({ id: 'newsQueue.richBody.link.tooltip' });
    const addLinkTitle = intl.formatMessage({ id: 'newsQueue.richBody.link.addTitle' });
    const editLinkTitle = intl.formatMessage({ id: 'newsQueue.richBody.link.editTitle' });
    const linkUrlLabel = intl.formatMessage({ id: 'newsQueue.richBody.link.urlLabel' });
    const linkUrlPlaceholder = intl.formatMessage({ id: 'newsQueue.richBody.link.urlPlaceholder' });
    const removeLinkLabel = intl.formatMessage({ id: 'newsQueue.richBody.link.remove' });
    const cancelLabel = intl.formatMessage({ id: 'newsQueue.common.cancel' });
    const saveLabel = intl.formatMessage({ id: 'newsQueue.common.save' });

    const turndownService = useMemo(
        () =>
            new TurndownService({
                headingStyle: 'atx',
                bulletListMarker: '-',
                codeBlockStyle: 'fenced',
                emDelimiter: '_',
                strongDelimiter: '**',
            }),
        []
    );

    const markdownToHtml = (markdown) => marked.parse(markdown || '', { gfm: false });

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ strike: false }),
            Link.configure({
                openOnClick: false,
                autolink: false,
                linkOnPaste: false,
            }),
        ],
        content: markdownToHtml(initialMarkdown),
        editorProps: {
            attributes: {
                class: 'rich-body-editor-content',
            },
        },
        onUpdate: ({ editor: nextEditor }) => {
            const markdown = turndownService.turndown(nextEditor.getHTML());
            onChangeMarkdown(markdown);
        },
    });

    useEffect(() => {
        if (!editor) return;
        const nextMarkdown = initialMarkdown || '';
        const currentMarkdown = turndownService.turndown(editor.getHTML());
        if (currentMarkdown === nextMarkdown) return;
        editor.commands.setContent(markdownToHtml(nextMarkdown), false);
    }, [editor, initialMarkdown, turndownService]);

    useEffect(() => {
        if (!linkDialogOpen) return;
        const timer = window.setTimeout(() => {
            linkUrlInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [linkDialogOpen]);

    const openLinkDialogForSelection = () => {
        if (!editor || !canEdit) return;
        const isLinkActive = editor.isActive('link');
        if (!isLinkActive && editor.state.selection.empty) return;

        if (isLinkActive) {
            editor.chain().focus().extendMarkRange('link').run();
        }

        setLinkUrlInput(editor.getAttributes('link').href || '');
        setLinkDialogOpen(true);
    };

    const closeLinkDialog = () => {
        setLinkDialogOpen(false);
        setLinkUrlInput('');
    };

    const applyLink = (event) => {
        event.preventDefault();
        if (!editor || !canEdit) return;
        const nextHref = linkUrlInput.trim();

        if (!nextHref) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            closeLinkDialog();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: nextHref }).run();
        closeLinkDialog();
    };

    const removeLink = () => {
        if (!editor || !canEdit) return;
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        closeLinkDialog();
    };

    const handleEditorClickCapture = (event) => {
        if (!editor || !canEdit) return;
        if (!(event.target instanceof Element)) return;

        const linkElement = event.target.closest('a[href]');
        if (!linkElement) return;

        event.preventDefault();
        editor.chain().focus().extendMarkRange('link').run();
        setLinkUrlInput(linkElement.getAttribute('href') || '');
        setLinkDialogOpen(true);
    };

    if (!editor) return null;

    const canOpenLinkDialog = editor.isActive('link') || !editor.state.selection.empty;
    const isLinkActive = editor.isActive('link');

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ p: 0.5 }}>
                <Tooltip title={boldLabel}>
                    <IconButton
                        size="small"
                        color={editor.isActive('bold') ? 'primary' : 'default'}
                        disabled={!canEdit}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={italicLabel}>
                    <IconButton
                        size="small"
                        color={editor.isActive('italic') ? 'primary' : 'default'}
                        disabled={!canEdit}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    >
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={linkButtonLabel}>
                    <span>
                        <IconButton
                            size="small"
                            color={isLinkActive ? 'primary' : 'default'}
                            disabled={!canEdit || !canOpenLinkDialog}
                            onClick={openLinkDialogForSelection}
                        >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Box sx={{ ml: 'auto', display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title={undoLabel}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!canEdit || !editor.can().chain().focus().undo().run()}
                                onClick={() => editor.chain().focus().undo().run()}
                            >
                                <UndoIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={redoLabel}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!canEdit || !editor.can().chain().focus().redo().run()}
                                onClick={() => editor.chain().focus().redo().run()}
                            >
                                <RedoIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Stack>
            <Divider />
            <Box
                sx={{
                    minHeight: 180,
                    p: 1.5,
                    '& .rich-body-editor-content': {
                        outline: 'none',
                    },
                    '& .rich-body-editor-content p': {
                        my: 1,
                    },
                    '& .rich-body-editor-content a': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: canEdit ? 'pointer' : 'text',
                    },
                }}
                onClickCapture={handleEditorClickCapture}
            >
                <EditorContent editor={editor} />
            </Box>
            <Dialog open={linkDialogOpen} onClose={closeLinkDialog} fullWidth maxWidth="sm">
                <Box component="form" onSubmit={applyLink}>
                    <DialogTitle>{isLinkActive ? editLinkTitle : addLinkTitle}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            inputRef={linkUrlInputRef}
                            fullWidth
                            label={linkUrlLabel}
                            margin="dense"
                            value={linkUrlInput}
                            onChange={(event) => setLinkUrlInput(event.target.value)}
                            placeholder={linkUrlPlaceholder}
                        />
                    </DialogContent>
                    <DialogActions>
                        {isLinkActive && (
                            <Button color="error" onClick={removeLink}>
                                {removeLinkLabel}
                            </Button>
                        )}
                        <Button onClick={closeLinkDialog}>{cancelLabel}</Button>
                        <Button type="submit" variant="contained" disabled={!canEdit} sx={{ color: 'common.white' }}>
                            {saveLabel}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
}

export default function RichBodyEditor({
    editableBodyField,
    effectiveArticleBody,
    editingField,
    editingValue,
    alwaysEditable = false,
    canEditFields,
    hasSavedEdit,
    beginEdit,
    cancelEdit,
    applyEdit,
    revertSavedField,
    editStartLabel,
    editApplyLabel,
    editDiscardLabel,
    editRevertSavedLabel,
    editUndoLabel,
    editRedoLabel,
    onEditingInput,
}) {
    const intl = useIntl();
    const isEditing = alwaysEditable ? canEditFields : editingField === editableBodyField;
    const undoLabel = editUndoLabel ?? intl.formatMessage({ id: 'newsQueue.edit.undo' });
    const redoLabel = editRedoLabel ?? intl.formatMessage({ id: 'newsQueue.edit.redo' });

    return (
        <Box className="editable-block-body">
            {isEditing ? (
                <RichMarkdownInput
                    initialMarkdown={alwaysEditable ? effectiveArticleBody : editingValue}
                    onChangeMarkdown={onEditingInput}
                    canEdit={canEditFields}
                    undoLabel={undoLabel}
                    redoLabel={redoLabel}
                />
            ) : (
                <ReactMarkdown>{effectiveArticleBody}</ReactMarkdown>
            )}
            <Stack spacing={0} direction="row" alignItems="center">
                {canEditFields && hasSavedEdit(editableBodyField) && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            verticalAlign: 'middle',
                            ml: 0.5,
                        }}
                    >
                        <EditNoteIcon fontSize="small" color="action" />
                    </Box>
                )}
                <Stack
                    className="edit-controls-body"
                    direction="row"
                    spacing={0}
                    sx={{
                        display: canEditFields ? 'inline-flex' : 'none',
                        mt: 0.5,
                        opacity: alwaysEditable ? 0 : (isEditing ? 1 : 0),
                        transition: 'opacity 0.15s ease',
                        '.editable-block-body:hover &': {
                            opacity: alwaysEditable
                                ? (hasSavedEdit(editableBodyField) ? 1 : 0)
                                : (canEditFields ? 1 : 0),
                        },
                    }}
                >
                    {alwaysEditable ? (
                        <>
                            {hasSavedEdit(editableBodyField) && (
                                <Tooltip title={editRevertSavedLabel}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={!canEditFields}
                                        onClick={() => revertSavedField(editableBodyField)}
                                    >
                                        <UndoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    ) : isEditing ? (
                        <>
                            <Tooltip title={editDiscardLabel}>
                                <IconButton size="small" color="error" onClick={cancelEdit} disabled={!canEditFields}>
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={editApplyLabel}>
                                <IconButton size="small" color="success" onClick={applyEdit} disabled={!canEditFields}>
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            {hasSavedEdit(editableBodyField) && (
                                <Tooltip title={editRevertSavedLabel}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={!canEditFields}
                                        onClick={() => revertSavedField(editableBodyField)}
                                    >
                                        <UndoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={editStartLabel}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!canEditFields}
                                    onClick={() => beginEdit(editableBodyField)}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}
